"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Share2 } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";

interface NativeShareButtonProps {
  floating?: boolean;
  iconOnly?: boolean;
  className?: string;
}

export default function NativeShareButton({
  floating = false,
  iconOnly = false,
  className,
}: NativeShareButtonProps) {
  const t = useTranslations("common.actions");
  const [feedback, setFeedback] = useState<string | null>(null);

  const handleShare = async () => {
    const shareData = {
      title: document.title,
      text: document.title,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setFeedback(t("shareDone"));
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
        setFeedback(t("linkCopied"));
      }
    } catch {
      setFeedback(null);
    }

    window.setTimeout(() => setFeedback(null), 1800);
  };

  return (
    <div
      className={cn(
        "relative flex items-center gap-2",
        floating &&
        "fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 sm:right-6 z-[70]",
        className
      )}
    >
      <Button
        type="button"
        variant={iconOnly ? "default" : "primary"}
        size={iconOnly ? "default" : "default"}
        className={cn(
          "cursor-target hidden md:block lg:block",
          iconOnly &&
          "h-14 w-14 rounded-full p-0 shadow-[0_10px_30px_rgba(255,185,70,0.35)] hover:scale-105 active:scale-95 transition-all duration-300 bg-gradient-to-br from-accent to-accent-hover text-primary border border-white/20 backdrop-blur"
        )}
        onClick={handleShare}
        aria-label={t("share")}
        title={t("share")}
      >
        {iconOnly ? <Share2 className="h-5 w-5" /> : t("share")}
      </Button>
      {feedback ? (
        <span
          role="status"
          className={cn(
            "pointer-events-none rounded-full border border-accent/30 bg-primary/90 px-3 py-1 text-xs text-accent shadow-lg backdrop-blur",
            iconOnly &&
            "absolute bottom-0 right-[calc(100%+0.75rem)] z-10 max-w-[min(240px,calc(100vw-5rem))] whitespace-normal sm:whitespace-nowrap"
          )}
        >
          {feedback}
        </span>
      ) : null}
    </div>
  );
}
